import React from 'react';
import type { MaterialAvailabilityResult } from '../types/production.types';

interface MaterialAvailabilityModalProps {
  isOpen: boolean;
  mode: 'gate' | 'readonly';
  orderLabel: string;
  isLoading: boolean;
  result: MaterialAvailabilityResult | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  // Justificación requerida por el endpoint de auditoría de overrides
  // (POST /:orderId/material-shortage-override) cuando se confirma un
  // movimiento habiendo faltante — solo aplica en modo 'gate' con faltante.
  note?: string;
  onNoteChange?: (val: string) => void;
}

const MaterialAvailabilityModal: React.FC<MaterialAvailabilityModalProps> = ({
  isOpen,
  mode,
  orderLabel,
  isLoading,
  result,
  isSubmitting,
  onClose,
  onConfirm,
  note = '',
  onNoteChange,
}) => {
  if (!isOpen) return null;

  const hasShortage = result?.checked && result.hasShortage;
  const allGood = result?.checked && !result.hasShortage;
  const unavailable = !isLoading && (!result || !result.checked);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between flex-shrink-0 ${hasShortage ? 'bg-red-600' : allGood ? 'bg-green-600' : 'bg-amber-500'} text-white`}>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              {isLoading ? '🔍 Verificando inventario...' : hasShortage ? '⚠️ Materiales con faltante' : allGood ? '✅ Inventario disponible' : '📦 Disponibilidad de Materiales'}
            </h2>
            <p className="text-sm opacity-90 mt-0.5">Orden #{orderLabel}</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-white hover:bg-opacity-20 transition-colors" title="Cerrar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-400 border-t-transparent"></div>
              <p className="text-sm">Consultando disponibilidad con Comercia...</p>
            </div>
          ) : unavailable ? (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-yellow-800 text-sm flex items-start gap-3">
              <span className="text-xl flex-shrink-0">ℹ️</span>
              <div>
                {result?.reason === 'NO_EXTERNAL_LINK'
                  ? 'Esta orden no está vinculada a Comercia — no hay disponibilidad que verificar.'
                  : 'No fue posible conectar con Comercia para verificar el inventario. Puedes continuar sin verificación.'}
              </div>
            </div>
          ) : (
            <>
              {/* Summary banner */}
              {allGood && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-3 text-green-800">
                  <span className="text-2xl">✅</span>
                  <span className="font-semibold">Todo el inventario requerido está disponible.</span>
                </div>
              )}
              {hasShortage && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-3 text-red-800">
                  <span className="text-2xl flex-shrink-0">⚠️</span>
                  <div>
                    <p className="font-semibold">Hay materiales con inventario insuficiente.</p>
                    <p className="text-sm mt-0.5 text-red-600">Las filas en rojo indican faltante. Puedes confirmar el movimiento de todas formas.</p>
                  </div>
                </div>
              )}

              {/* Table */}
              {result && result.items.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                        <th className="px-3 py-2 text-left font-semibold">Código</th>
                        <th className="px-3 py-2 text-left font-semibold">Descripción</th>
                        <th className="px-3 py-2 text-center font-semibold">Calibre</th>
                        <th className="px-3 py-2 text-center font-semibold">Factor</th>
                        <th className="px-3 py-2 text-center font-semibold">Medida</th>
                        <th className="px-3 py-2 text-right font-semibold">Requerido</th>
                        <th className="px-3 py-2 text-right font-semibold">Disponible</th>
                        <th className="px-3 py-2 text-right font-semibold">Faltante</th>
                        <th className="px-3 py-2 text-center font-semibold">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {result.items.map((item, idx) => {
                        const short = item.disponible < item.cantidad;
                        return (
                          <tr key={idx} className={short ? 'bg-red-50' : 'bg-white'}>
                            <td className="px-3 py-2 font-mono text-xs text-gray-700">{item.codigo}</td>
                            <td className="px-3 py-2 text-gray-800">{item.descripcion}</td>
                            <td className="px-3 py-2 text-center text-gray-700 font-mono text-xs">{item.caliber || <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-center text-gray-700 font-mono text-xs">{item.factor || <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-center text-gray-700 font-mono text-xs">{item.medida || <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{item.cantidad} <span className="text-gray-400 text-xs">{item.unidad}</span></td>
                            <td className="px-3 py-2 text-right text-gray-700">{item.disponible} <span className="text-gray-400 text-xs">{item.unidad}</span></td>
                            <td className={`px-3 py-2 text-right font-semibold ${short ? 'text-red-600' : 'text-gray-300'}`}>
                              {short ? `${item.faltante} ${item.unidad}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {short
                                ? <span className="inline-block bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">Faltante</span>
                                : <span className="inline-block bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">OK</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-6">No hay materiales registrados para esta orden.</p>
              )}

              {mode === 'gate' && hasShortage && onNoteChange && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Justificación <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <textarea
                    value={note}
                    onChange={e => onNoteChange(e.target.value)}
                    placeholder="Explica por qué se inicia esta orden pese al faltante de material..."
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400"
                    rows={2}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {mode === 'gate' ? 'Cancelar movimiento' : 'Cerrar'}
          </button>
          {mode === 'gate' && onConfirm && (
            <button
              onClick={onConfirm}
              disabled={isLoading || isSubmitting}
              className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSubmitting && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              )}
              {isSubmitting ? 'Aplicando...' : 'Confirmar movimiento'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialAvailabilityModal;
